import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ToastNotification from './ToastNotification';
import './components.css';

const GroupManagement = ({ user, hasFeatureAccess }) => {
  const navigate = useNavigate();
  const [groupData, setGroupData] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showChildModal, setShowChildModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Form states
  const [childName, setChildName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [studentName, setStudentName] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [groupType, setGroupType] = useState('family');

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: '', type: 'info' });
  };

  useEffect(() => {
    loadGroupData();
  }, []);

  const loadGroupData = async () => {
    try {
      setLoading(true);
      
      // Load group members
      const membersResponse = await axios.get('/api/group/members');
      if (membersResponse.data.success) {
        setMembers(membersResponse.data.members);
      }
      
      // Load invitations
      const invitationsResponse = await axios.get('/api/group/invitations');
      if (invitationsResponse.data.success) {
        setInvitations(invitationsResponse.data.invitations);
      }
      
      // Load group subscription data
      const subscriptionResponse = await axios.get('/api/group/subscription-status');
      if (subscriptionResponse.data.success) {
        setGroupData(subscriptionResponse.data);
      }
      
    } catch (err) {
      console.error('Error loading group data:', err);
      setError('Failed to load group data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChildAccount = async () => {
    if (!childName.trim()) {
      showToast('Please enter a name for the child account', 'warning');
      return;
    }

    try {
      const response = await axios.post('/api/group/create-child', {
        child_name: childName,
        group_type: groupType
      });

      if (response.data.success) {
        setShowChildModal(false);
        setChildName('');
        await loadGroupData();

        // Show success message - details sent via email
        showToast(`✅ Child account created! Login details sent to ${response.data.username}@your-email.com`, 'success');
      } else {
        showToast('Failed to create child account: ' + response.data.error, 'error');
      }
    } catch (err) {
      console.error('Error creating child account:', err);
      showToast('Failed to create child account. Please try again.', 'error');
    }
  };

  const handleSendInvitation = async () => {
    if (!studentEmail.trim()) {
      showToast('Please enter a student email address', 'warning');
      return;
    }

    try {
      const response = await axios.post('/api/group/invite-student', {
        student_email: studentEmail,
        student_name: studentName,
        custom_message: customMessage,
        group_type: groupType
      });

      if (response.data.success) {
        setShowInviteModal(false);
        setStudentEmail('');
        setStudentName('');
        setCustomMessage('');
        await loadGroupData();

        showToast(`📧 Invitation sent to ${studentEmail}!`, 'success');
      } else {
        showToast('Failed to send invitation: ' + response.data.error, 'error');
      }
    } catch (err) {
      console.error('Error sending invitation:', err);
      showToast('Failed to send invitation. Please try again.', 'error');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const response = await axios.post(`/api/group/resend-invitation/${invitationId}`);
      if (response.data.success) {
        showToast('📧 Invitation resent successfully!', 'success');
      } else {
        showToast('Failed to resend invitation: ' + response.data.error, 'error');
      }
    } catch (err) {
      console.error('Error resending invitation:', err);
      showToast('Failed to resend invitation. Please try again.', 'error');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      const response = await axios.delete(`/api/group/invitation/${invitationId}`);
      if (response.data.success) {
        await loadGroupData();
        showToast('Invitation cancelled', 'success');
      } else {
        showToast('Failed to cancel invitation: ' + response.data.error, 'error');
      }
    } catch (err) {
      console.error('Error cancelling invitation:', err);
      showToast('Failed to cancel invitation. Please try again.', 'error');
    }
  };

  const handleViewProgress = (member) => {
    // Navigate to parent dashboard with selected child
    navigate(`/parent?child=${member.id}`);
  };

  const handleResetPassword = (member) => {
    setSelectedMember(member);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleConfirmPasswordReset = async () => {
    if (!selectedMember) return;

    try {
      const response = await axios.post('/api/group/reset-child-password', {
        child_user_id: selectedMember.id,
        new_password: newPassword || undefined // If empty, generate random
      });

      if (response.data.success) {
        setShowPasswordModal(false);
        setSelectedMember(null);
        setNewPassword('');

        if (response.data.new_password) {
          // Show generated password
          showToast(`Password reset! New password: ${response.data.new_password}`, 'success');
        } else {
          showToast('Password reset successfully!', 'success');
        }
      } else {
        showToast('Failed to reset password: ' + response.data.error, 'error');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      showToast('Failed to reset password. Please try again.', 'error');
    }
  };

  if (!hasFeatureAccess('GROUP_MANAGEMENT')) {
    return (
      <div className="group-management">
        <div className="upgrade-prompt">
          <h3>Manage Up to 5 Learners Under One Plan</h3>
          <p>Perfect for families, tutoring groups, homeschool pods, or small classrooms. Get 5 Unlimited accounts, easily invite members by email, track progress, and manage everything from one dashboard - just $199/year.</p>
          <button
            className="upgrade-button"
            onClick={() => window.location.href = '/pricing'}
          >
            Upgrade to Home Plan
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="group-management">
        <div className="loading">Loading group data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="group-management">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const totalSlots = groupData?.max_family_members || 5;
  const usedSlots = members.length + invitations.filter(inv => !inv.is_used && !inv.is_expired).length;
  const availableSlots = totalSlots - usedSlots;

  const slotPercentage = (usedSlots / totalSlots) * 100;

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 249, 250, 0.98))',
      backdropFilter: 'blur(20px)',
      border: '2px solid rgba(14, 165, 233, 0.15)',
      borderRadius: '20px',
      padding: '32px',
      marginBottom: '24px',
      boxShadow: '0 8px 32px rgba(14, 165, 233, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04)'
    }}>
      {/* Modern Header - iOS/Stripe Style */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#0F172A',
            margin: '0 0 8px 0',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}>
            👥 Group Management
          </h2>
          <p style={{
            fontSize: '15px',
            color: '#64748B',
            margin: 0,
            fontWeight: '500'
          }}>
            Manage your {totalSlots} learner accounts
          </p>
        </div>

        {/* Capacity Indicator - Stripe Style */}
        <div style={{
          background: 'rgba(14, 165, 233, 0.08)',
          borderRadius: '12px',
          padding: '16px 20px',
          minWidth: '200px'
        }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#64748B',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Account Capacity
          </div>
          <div style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#0EA5E9',
            marginBottom: '12px'
          }}>
            {usedSlots} / {totalSlots}
          </div>
          {/* Progress Bar - iOS Style */}
          <div style={{
            width: '100%',
            height: '6px',
            background: 'rgba(14, 165, 233, 0.1)',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${slotPercentage}%`,
              height: '100%',
              background: slotPercentage >= 100 ? 'linear-gradient(90deg, #EF4444, #F97316)' : 'linear-gradient(90deg, #0EA5E9, #3B82F6)',
              borderRadius: '10px',
              transition: 'width 0.3s ease'
            }} />
          </div>
          {availableSlots > 0 && (
            <div style={{
              fontSize: '13px',
              color: '#10B981',
              marginTop: '8px',
              fontWeight: '600'
            }}>
              {availableSlots} slot{availableSlots !== 1 ? 's' : ''} available
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons - Discord/Google Meet Style */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setShowChildModal(true)}
          disabled={availableSlots <= 0}
          style={{
            background: availableSlots <= 0 ? 'rgba(100, 116, 139, 0.2)' : 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
            color: availableSlots <= 0 ? '#94A3B8' : 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '14px 24px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: availableSlots <= 0 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            boxShadow: availableSlots <= 0 ? 'none' : '0 4px 12px rgba(14, 165, 233, 0.25)',
            opacity: availableSlots <= 0 ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (availableSlots > 0) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 16px rgba(14, 165, 233, 0.35)';
            }
          }}
          onMouseLeave={(e) => {
            if (availableSlots > 0) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.25)';
            }
          }}
        >
          <span style={{ fontSize: '18px' }}>➕</span>
          Add Child Account
        </button>
        <button
          onClick={() => setShowInviteModal(true)}
          disabled={availableSlots <= 0}
          style={{
            background: availableSlots <= 0 ? 'rgba(100, 116, 139, 0.1)' : 'white',
            color: availableSlots <= 0 ? '#94A3B8' : '#0EA5E9',
            border: availableSlots <= 0 ? '2px solid rgba(100, 116, 139, 0.2)' : '2px solid #0EA5E9',
            borderRadius: '12px',
            padding: '14px 24px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: availableSlots <= 0 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            opacity: availableSlots <= 0 ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (availableSlots > 0) {
              e.target.style.background = 'rgba(14, 165, 233, 0.05)';
              e.target.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseLeave={(e) => {
            if (availableSlots > 0) {
              e.target.style.background = 'white';
              e.target.style.transform = 'translateY(0)';
            }
          }}
        >
          <span style={{ fontSize: '18px' }}>📧</span>
          Invite Student
        </button>
      </div>

      {availableSlots <= 0 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(249, 115, 22, 0.08))',
          border: '2px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#DC2626', marginBottom: '4px' }}>
              All slots in use
            </div>
            <div style={{ fontSize: '14px', color: '#64748B' }}>
              Remove a student or cancel an invitation to add more learners
            </div>
          </div>
        </div>
      )}

      {/* Members Section - Modern Card Grid */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#0F172A',
          marginBottom: '16px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}>
          Your Students
        </h3>
        {members.length === 0 ? (
          <div style={{
            background: 'rgba(148, 163, 184, 0.05)',
            border: '2px dashed rgba(148, 163, 184, 0.3)',
            borderRadius: '16px',
            padding: '48px 24px',
            textAlign: 'center',
            color: '#64748B'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
              No students added yet
            </div>
            <div style={{ fontSize: '14px' }}>
              Use the buttons above to add child accounts or invite students
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {members.map(member => (
              <div key={member.id} style={{
                background: 'white',
                border: '2px solid rgba(14, 165, 233, 0.1)',
                borderRadius: '16px',
                padding: '20px',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(14, 165, 233, 0.12)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                  {/* Avatar - Discord Style */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #0EA5E9, #3B82F6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    flexShrink: 0
                  }}>
                    👤
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#0F172A',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {member.name || member.username}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#64748B',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {member.username}
                    </div>
                  </div>
                  {member.is_account_manager && (
                    <span style={{
                      background: 'linear-gradient(135deg, #10B981, #059669)',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '700',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Admin
                    </span>
                  )}
                </div>

                {/* Status Badge */}
                {!member.is_account_manager && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    fontSize: '13px',
                    color: '#64748B'
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: member.last_login ? '#10B981' : '#94A3B8',
                      flexShrink: 0
                    }} />
                    {member.last_login ? `Active ${member.last_login}` : 'Never logged in'}
                  </div>
                )}

                {/* Action Buttons */}
                {!member.is_account_manager && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <button
                      onClick={() => handleViewProgress(member)}
                      style={{
                        flex: 1,
                        background: 'rgba(14, 165, 233, 0.08)',
                        color: '#0EA5E9',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(14, 165, 233, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(14, 165, 233, 0.08)';
                      }}
                    >
                      View Progress
                    </button>
                    <button
                      onClick={() => handleResetPassword(member)}
                      style={{
                        background: 'rgba(100, 116, 139, 0.08)',
                        color: '#64748B',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '20px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(100, 116, 139, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(100, 116, 139, 0.08)';
                      }}
                      title="Reset Password"
                    >
                      🔑
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invitations Section - Modern Style */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#0F172A',
          marginBottom: '16px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}>
          Pending Invitations
        </h3>
        {invitations.filter(inv => !inv.is_used).length === 0 ? (
          <div style={{
            background: 'rgba(148, 163, 184, 0.05)',
            border: '2px dashed rgba(148, 163, 184, 0.3)',
            borderRadius: '16px',
            padding: '32px 24px',
            textAlign: 'center',
            color: '#64748B'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📧</div>
            <div style={{ fontSize: '14px', fontWeight: '500' }}>
              No pending invitations. Use "Invite Student" to send invitation emails.
            </div>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {invitations.filter(inv => !inv.is_used).map(invitation => (
              <div key={invitation.id} style={{
                background: 'white',
                border: invitation.is_expired ? '2px solid rgba(239, 68, 68, 0.2)' : '2px solid rgba(14, 165, 233, 0.1)',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#0F172A',
                    marginBottom: '4px'
                  }}>
                    {invitation.email || 'No email'}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: invitation.is_expired ? '#DC2626' : '#10B981',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: invitation.is_expired ? '#DC2626' : '#10B981'
                    }} />
                    {invitation.is_expired ? 'Expired' : 'Pending signup'}
                    <span style={{ color: '#94A3B8', marginLeft: '8px' }}>
                      Sent {new Date(invitation.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                {!invitation.is_expired && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleResendInvitation(invitation.id)}
                      style={{
                        background: 'rgba(14, 165, 233, 0.08)',
                        color: '#0EA5E9',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      style={{
                        background: 'rgba(239, 68, 68, 0.08)',
                        color: '#DC2626',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {invitation.is_expired && (
                  <button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    style={{
                      background: 'rgba(100, 116, 139, 0.08)',
                      color: '#64748B',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Child Account Creation Modal - Modern Style */}
      {showChildModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '440px',
            boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px 24px 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#0F172A',
                  margin: '0 0 4px 0'
                }}>
                  ➕ Create Child Account
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#64748B',
                  margin: 0
                }}>
                  Create an account you'll manage for your child
                </p>
              </div>
              <button
                onClick={() => setShowChildModal(false)}
                style={{
                  background: 'rgba(100, 116, 139, 0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#64748B'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Child's Name
                </label>
                <input
                  type="text"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  placeholder="Enter child's name"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #E2E8F0',
                    borderRadius: '10px',
                    fontSize: '15px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0EA5E9'}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>

              <div style={{
                background: 'rgba(14, 165, 233, 0.08)',
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '4px' }}>
                  Username will be:
                </div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#0EA5E9' }}>
                  {user?.username}_child{members.filter(m => m.username?.includes('_child')).length + 1}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px 24px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowChildModal(false)}
                style={{
                  padding: '12px 20px',
                  background: 'white',
                  color: '#64748B',
                  border: '2px solid #E2E8F0',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateChildAccount}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #0EA5E9, #0284C7)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)'
                }}
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Invitation Modal - Modern Style */}
      {showInviteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '480px',
            boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px 24px 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#0F172A',
                  margin: '0 0 4px 0'
                }}>
                  📧 Invite Student
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#64748B',
                  margin: 0
                }}>
                  Send an invitation for them to create their own account
                </p>
              </div>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  background: 'rgba(100, 116, 139, 0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#64748B'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Student's Email <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  type="email"
                  value={studentEmail}
                  onChange={(e) => setStudentEmail(e.target.value)}
                  placeholder="student@email.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #E2E8F0',
                    borderRadius: '10px',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0EA5E9'}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Student's Name <span style={{ color: '#94A3B8', fontWeight: '400' }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="Their name"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #E2E8F0',
                    borderRadius: '10px',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0EA5E9'}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Personal Message <span style={{ color: '#94A3B8', fontWeight: '400' }}>(optional)</span>
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Add a personal note to the invitation..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #E2E8F0',
                    borderRadius: '10px',
                    fontSize: '15px',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0EA5E9'}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px 24px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  padding: '12px 20px',
                  background: 'white',
                  color: '#64748B',
                  border: '2px solid #E2E8F0',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvitation}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #0EA5E9, #0284C7)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)'
                }}
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal - Modern Style */}
      {showPasswordModal && selectedMember && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px 24px 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#0F172A',
                  margin: '0 0 4px 0'
                }}>
                  🔑 Reset Password
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#64748B',
                  margin: 0
                }}>
                  Reset password for {selectedMember.name || selectedMember.username}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setSelectedMember(null);
                  setNewPassword('');
                }}
                style={{
                  background: 'rgba(100, 116, 139, 0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#64748B'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  New Password <span style={{ color: '#94A3B8', fontWeight: '400' }}>(leave blank to auto-generate)</span>
                </label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password or leave blank"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #E2E8F0',
                    borderRadius: '10px',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0EA5E9'}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>

              <div style={{
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '10px',
                padding: '12px 16px',
                fontSize: '13px',
                color: '#92400E'
              }}>
                ⚠️ The new password will be shown once. Make sure to save it or share it with the student.
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px 24px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setSelectedMember(null);
                  setNewPassword('');
                }}
                style={{
                  padding: '12px 20px',
                  background: 'white',
                  color: '#64748B',
                  border: '2px solid #E2E8F0',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPasswordReset}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                }}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
          show={toast.show}
        />
      )}
    </div>
  );
};

export default GroupManagement;